import { useQuery } from '@apollo/client/react';
import type { IntrospectionEnumType } from 'graphql';
import { List } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import {
  AutocompleteTextField,
  FilterAutocomplete,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { getDictionaryLabel } from '@modules/_shared/utils/dictionaries';

const GET_ENUMS = gql(/* GraphQL */ `
  query GetEnums {
    dictionaries(where: { type: { _eq: enums } }) {
      name
      label
    }
  }
`);

export interface EnumFilterProps extends QuickFilterProps {
  type: IntrospectionEnumType;
}

interface EnumFilterOption {
  value: string;
  label: string;
}

export const EnumFilter = ({
  type,
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  getValueFromPath,
  onChange,
  queryParamsScope,
  disabled,
  excludeMode = false,
  enumValueLabel,
}: EnumFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { data } = useQuery(GET_ENUMS);
  const enumsDict = data?.dictionaries;

  // get table name by removing ending _enum from type name, sometime it ends by _enum_enum and we need to keep only one
  const tableName = type.name.replace(/_enum$/, '');
  const defaultValue: string[] | undefined = getValueFromPath(path, where);

  if (!type.enumValues) {
    throw new Error('Type is not an enum');
  }

  const options: EnumFilterOption[] = type.enumValues.map(v => {
    const dictionaryLabel = getDictionaryLabel(
      `${tableName}.${v.name}`,
      enumsDict,
      '',
    );
    return {
      value: v.name,
      label:
        dictionaryLabel.length > 0
          ? dictionaryLabel
          : enumValueLabel?.(v.name) ?? v.name,
    };
  });

  const defaultOptions: EnumFilterOption[] = excludeMode
    ? options.filter(opt => !defaultValue?.includes(opt.value))
    : options.filter(opt => defaultValue?.includes(opt.value));

  const handleChange = (selectedOptions: EnumFilterOption[]) => {
    const values = excludeMode
      ? options
          .filter(
            opt =>
              !selectedOptions.find(selected => selected.value === opt.value),
          )
          .map(option => option.value)
      : selectedOptions.map(option => option.value) ?? [];
    let newWhere = deleteWhereClause(where, path, true);
    if (values.length > 0) {
      newWhere = addWhereClause(newWhere, path, values);
    }

    setFiltersParams(onChange?.(values, newWhere) ?? newWhere);
  };

  const chipLabel = (() => {
    switch (defaultOptions.length) {
      case 0:
        return label;
      case 1:
        return `${label}: ${defaultOptions[0].label}`;
      default:
        return `${label}: ${defaultOptions[0].label} +${
          defaultOptions.length - 1
        }`;
    }
  })();

  return (
    <FilterChip
      label={chipLabel}
      icon={<List size={16} />}
      onDelete={defaultOptions.length > 0 ? () => handleChange([]) : undefined}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <FilterAutocomplete<EnumFilterOption>
          options={options}
          onClose={handleClose}
          onChange={(_e, option) => handleChange(option)}
          value={defaultOptions ?? []}
          renderInput={AutocompleteTextField}
        />
      )}
    />
  );
};
